const MAX_TEXT = 500;
const MAX_LIST = 8;
const UNSAFE_TEXT = /raw\s*(prompt|provider\s*response|payload)|provider\s*response|supabase|\bsql\b|table\s*name|token|cost|header|stack\s*trace|request\s*(id|metadata)|service[- ]?role|bearer|cookie|fixture|debug/i;

function text(value, fallback = "") {
  const normalized = String(value ?? "").replace(/\s+/g, " ").trim();
  return !normalized || UNSAFE_TEXT.test(normalized) ? fallback : normalized.slice(0, MAX_TEXT);
}

function list(value, fallback = []) {
  if (!Array.isArray(value)) return fallback;
  return Array.from(new Set(value.map((item) => text(item)).filter(Boolean))).slice(0, MAX_LIST);
}

function keyPart(value, fallback) {
  return text(value, fallback).toLowerCase().replace(/[^a-z0-9._-]+/g, "-");
}

export function buildContentBriefIdempotencyKey(input = {}) {
  return [
    "content-brief",
    keyPart(input.accountId || input.account_id, "account-unavailable"),
    keyPart(input.runId || input.run_id, "run-unavailable"),
    keyPart(input.recommendationId || input.recommendation_id || input.recommendation?.id || input.recommendation?.key, "recommendation-unavailable"),
    keyPart(input.actionBriefId || input.action_brief_id || input.actionBrief?.id, "action-brief-required"),
    keyPart(input.outputType || "Content Brief", "content-brief"),
  ].join(":");
}

export function buildContentBriefGenerationModel(input = {}) {
  const source = input && typeof input === "object" ? input : {};
  const actionBrief = source.actionBrief && typeof source.actionBrief === "object" ? source.actionBrief : {};
  const contentBrief = actionBrief.content_brief && typeof actionBrief.content_brief === "object" ? actionBrief.content_brief : {};
  const structure = actionBrief.suggested_structure && typeof actionBrief.suggested_structure === "object" ? actionBrief.suggested_structure : {};
  const actionBriefReady = Boolean(source.actionBriefReady || source.actionBriefId || actionBrief.id || actionBrief.generation_status === "generated");
  const idempotencyKey = buildContentBriefIdempotencyKey(source);
  if (!actionBriefReady) {
    return { status: "blocked", reason: "requires_action_brief", idempotencyKey, contentBrief: null };
  }

  const productArea = String(source.productArea || actionBrief.product_area || "competitive").toLowerCase() === "local" ? "local" : "competitive";
  const accountName = text(source.accountName || source.account_name, "The business");
  const recommendationTitle = text(source.recommendationTitle || source.recommendation_title || source.recommendation?.title || actionBrief.title, "Reviewed recommendation");
  const outputType = "Content Brief";
  const targetAsset = text(source.targetAsset || source.target_asset || contentBrief.recommended_asset || structure.h1, "Reviewed content asset");
  const targetAudience = text(source.targetAudience || source.target_audience || contentBrief.audience, productArea === "local" ? "People evaluating local services in the target area" : "Buyers evaluating this category and its alternatives");
  const evidencePattern = text(source.evidencePattern || source.evidence_pattern || contentBrief.opportunity_summary || actionBrief.why_this_matters, "The reviewed recommendation identifies a visibility or recommendation gap.");
  const objective = text(source.objective || contentBrief.objective || actionBrief.recommended_action, "Strengthen the asset that addresses the reviewed opportunity.");
  const expectedResult = text(source.expectedResult || source.expected_result, productArea === "local" ? `A clearer local asset that helps people understand the service, category, and location.` : `A clearer asset that helps buyers evaluate the business in this category.`);
  const suggestedAngle = text(source.suggestedAngle || source.suggested_angle || contentBrief.key_points?.[0] || actionBrief.recommended_action, "Lead with the reviewed customer decision and support it with verified proof.");
  const outline = list(source.outline || structure.sections, ["Audience and decision context", "What the business offers", "Decision criteria", "Verified proof points", "Common questions", "Next step"]);
  const recommendedSections = list(source.recommendedSections || source.recommended_sections || outline, outline);
  const faqs = list(source.FAQsToAnswer || source.faqsToAnswer || source.faqs_to_answer || contentBrief.suggested_faqs, ["What should someone know before choosing this option?", "What proof should a buyer review?", "What makes this business relevant to the need?"]);
  const claims = list(source.claimsToSupport || source.claims_to_support || contentBrief.key_points, ["The business serves the stated audience.", "The recommended asset addresses the reviewed opportunity."]);
  const proofPoints = list(source.proofPointsNeeded || source.proof_points_needed || contentBrief.known_facts, ["Verified customer or project proof", "Accurate service, category, or product details", "Evidence that supports the recommended claim"]);
  const schemaNotes = list(source.schemaOrPageNotes || source.schema_or_page_notes || structure.schema_recommendations, ["Use only accurate page and structured-data fields that can be verified."]);

  return {
    status: "ready",
    idempotencyKey,
    contentBrief: {
      title: text(source.title, `${accountName} · ${targetAsset}`),
      accountName,
      recommendationTitle,
      outputType,
      targetAsset,
      targetAudience,
      buyerIntent: productArea === "competitive" ? "Compare, evaluate, and shortlist the right option." : null,
      localIntent: productArea === "local" ? "Find relevant local services in the target area." : null,
      evidencePattern,
      objective,
      expectedResult,
      suggestedAngle,
      outline,
      recommendedSections,
      FAQsToAnswer: faqs,
      claimsToSupport: claims,
      proofPointsNeeded: proofPoints,
      schemaOrPageNotes: schemaNotes,
      internalNotesExcludedFromCustomerView: true,
      generatedAt: source.generatedAt || source.generated_at || null,
      idempotencyKey,
    },
  };
}
